import { useAuth0 } from '@auth0/auth0-react';
import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { TokenCustomClaimKeysEnum } from '@sitecore-ui/portal-singular';
import { env } from 'features/common/config/envConfig';
import { IconLinks } from 'features/common/utils/icons';
import NoTenantsPanelMessage from 'features/home/components/homepage/components/NoTenantsPanelMessage';
import { useGetCurrentOrg } from 'features/home/services/identity/organization';
import { selectApp, useMainStateDispatch } from 'features/main/context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import {
  getUnifiedTrackingApiBaseUrl,
  wsAuthorize,
  wsOrgConfig,
  wsTenantConfig,
} from 'features/unified-tracking/services/unified-tracking/unifiedTracking.service';
import {
  Destination,
  IAuthUnifiedTrackingResponse,
  IAuthUnifiedTrackingResponseType,
  IConfigUnifiedTrackingResponse,
  IOrgUnifiedTrackingResponse,
  ISiteUnifiedTracking,
  IUnifiedTrackingResponse,
} from 'features/unified-tracking/services/unified-tracking/unifiedTrackingModel';
import { ContextUserApplication, useGetTenantsQuery } from 'gql/graphql';
import { range } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DestinationCard } from './components/DestinationCard';
import { SiteCard } from './components/SiteCard';
import {
  filterApps,
  getCustomer,
  getCustomerEnvironmentType,
  getRelatedDestinations,
  getRelatedSites,
  getTenantSitesAndDestinations,
} from './utils/utils';

const UnifiedTracking = () => {
  const [, dispatch] = useMainStateDispatch();
  const { isLoading: isGetCurrentOrganizationLoading } = useGetCurrentOrg();
  const { getAccessTokenSilently, user } = useAuth0();
  const organizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];
  let orgData: IOrgUnifiedTrackingResponse;
  const uuid = uuidv4();

  const [webSocket, setWebSocket] = useState<WebSocket>();
  const [sitesQueryLoading, setSitesQueryLoading] = useState(true);
  const [destinationsQueryLoading, setDestinationsQueryLoading] =
    useState(true);
  const [tenantData, setTenantData] = useState(
    new Map<string, IConfigUnifiedTrackingResponse>(),
  );
  const [filteredDest, setFilteredDest] = useState<Array<any>>([]);
  const [sites, setSites] = useState<ISiteUnifiedTracking[]>([]);
  const [destinationsFlow, setDestinationsFlow] = useState<
    Map<string, Destination>
  >(new Map<string, Destination>());
  const [selectedSite, setSelectedSites] = useState<any>();
  const [selectedDestination, setSelectedDestination] = useState<any>();

  // Establish WebSocket connection
  const connectToWebSocket = () => {
    const serverUrl = getUnifiedTrackingApiBaseUrl();
    const ws = new WebSocket(serverUrl);
    setWebSocket(ws);
    let intervalRenew: NodeJS.Timeout;

    ws.addEventListener('message', (event) => {
      wsMessages(ws, JSON.parse(event.data));
    });

    ws.addEventListener('open', async () => {
      const token = await getAccessTokenSilently({
        organization_id: organizationId,
      });

      wsAuthorize(ws, organizationId, token, uuid);

      intervalRenew = setInterval(() => {
        wsAuthorize(ws, organizationId, token, uuid);
      }, 30 * 1000);
    });

    // Similar onopen, onmessage, onerror from previous examples
    ws.onclose = (reason) => {
      if (reason.code < 4000) {
        console.log(
          'Connection closed. Attempting to reconnect...',
          reason.code,
        );
        setTimeout(connectToWebSocket, 3000); // Reconnect after 3 seconds
      } else {
        clearInterval(intervalRenew);
      }
    };
  };

  useEffect(() => {
    if (!webSocket) {
      connectToWebSocket();
    }

    return () => {
      // Clean up WebSocket on component unmount
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        webSocket.close(4000);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocket]);

  const wsMessages = (
    ws: WebSocket,
    response: IUnifiedTrackingResponse<
      | IAuthUnifiedTrackingResponse
      | IOrgUnifiedTrackingResponse
      | IConfigUnifiedTrackingResponse
    >,
  ) => {
    if (response.context.clientRequestId.startsWith('request-auth-')) {
      const data = response.data as IAuthUnifiedTrackingResponse;
      if (
        data.id ===
          IAuthUnifiedTrackingResponseType.connectionAuthorizationDenied ||
        response.result === 'ERROR'
      ) {
        console.error(data.reason);
      } else if (!orgData) {
        wsOrgConfig(ws, organizationId, uuid);
      }
    } else if (
      response.context.clientRequestId.startsWith('request-organization-base-')
    ) {
      orgData = response.data as IOrgUnifiedTrackingResponse;

      if (response.result === 'OK' && orgData.tenantIds?.length) {
        for (let index = 0; index < orgData.tenantIds.length; index++) {
          const tenant = orgData.tenantIds[index];
          wsTenantConfig(ws, organizationId, tenant, uuid);
        }
      } else {
        setSitesQueryLoading(false);
        setDestinationsQueryLoading(false);
      }
    } else if (
      response.context.clientRequestId.startsWith('request-config-tenant-')
    ) {
      const data = response.data as IConfigUnifiedTrackingResponse;

      if (response.result === 'ERROR') {
        tenantData.set(response.context.clientRequestId, data);
        setTenantData(tenantData);
      } else {
        tenantData.set(data.tenant?.id, data);
        setTenantData(tenantData);
      }

      if (tenantData.size === orgData.tenantIds.length) {
        const { sitesUnique, destinationUnique } =
          getTenantSitesAndDestinations(tenantData);

        setSites(sitesUnique);
        setSitesQueryLoading(false);

        setDestinationsFlow(destinationUnique);
        setDestinationsQueryLoading(false);
      }
    }
  };

  const { data: tenantsQueryData, loading: tenantsQueryLoading } =
    useGetTenantsQuery({
      variables: {
        first: 100,
        env,
      },
      notifyOnNetworkStatusChange: true,
    });

  const edges = tenantsQueryData?.user?.applications?.edges ?? [];
  const nodesApps = tenantsQueryData?.user?.applications?.nodes ?? [];

  const applicationsViews = edges.map((edge) => {
    const { id, appearance } = edge.node;
    const aNode = appearance?.web?.actions?.nodes?.[0];
    const application = nodesApps.find((node) => node.id === id);

    return {
      tenant: getCustomer(id, nodesApps),
      displayName: aNode?.displayName ?? application?.displayName,
      name: aNode?.name ?? application?.name,
      id,
      icon: aNode?.icon,
      customerEnvironmentType: getCustomerEnvironmentType(id, nodesApps),
    };
  });

  useEffect(() => {
    setFilteredDest(filterApps(applicationsViews, destinationsFlow));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantsQueryLoading, destinationsQueryLoading]);

  const siteOnHover = (site: ISiteUnifiedTracking | null, index?: number) => {
    if (site) {
      const destinations: {
        tenant: any;
        displayName: any;
        name: any;
        id: string;
        icon: any;
        customerEnvironmentType: string;
      }[] = [];
      const apps = filteredDest;

      const selectedDestRelations = getRelatedDestinations(
        tenantData,
        site,
        destinations,
        apps,
        index,
      );

      setSelectedSites(selectedDestRelations);
    } else {
      setSelectedSites(null);
    }
  };

  const destinationOnHover = (
    destination: {
      tenant: any;
      displayName: string;
      name: string;
      id: string;
      icon: string;
      customerEnvironmentType: string;
    } | null,
    index?: number,
  ) => {
    if (destination && (index || index === 0)) {
      const selectedSiteRelations = getRelatedSites(
        destination,
        tenantData,
        sites,
        index,
      );

      setSelectedDestination(selectedSiteRelations);
    } else {
      setSelectedDestination(null);
    }
  };

  const siteOnClick = (tenantId: ISiteUnifiedTracking) => {
    console.log('site click', tenantId);
  };

  const destinationOnClick = (tenantId: string) => {
    dispatch(
      selectApp({
        app: nodesApps.find(
          (node) => node.id === tenantId,
        ) as ContextUserApplication,
      }),
    );
    dispatch(openDrawer('tenant-drawer'));
  };

  return (
    <Box
      h='full'
      overflow='auto'
      layerStyle='section.main'
      bg='chakra-subtle-bg'
    >
      <Flex direction='column' mb='10' gap='1'>
        <Heading as='h1'>
          Unified Tracking
          <Badge
            colorScheme='warning'
            size='lg'
            fontWeight='semibold'
            textTransform='uppercase'
            marginLeft='3'
          >
            preview
          </Badge>
          <Skeleton
            isLoaded={!isGetCurrentOrganizationLoading}
            //@ts-ignore
            display='inline-block'
          ></Skeleton>
        </Heading>
        {/* <Text variant='subtle'>
          Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Donec odio.
          Quisque volutpat mattis eros. Nullam malesuada erat ut turpis.
          <Link color='primary.500' href='#' isExternal>
            Learn more
          </Link>
          <Skeleton
            isLoaded={!isGetCurrentOrganizationLoading}
            //@ts-ignore
            display='inline-block'
          ></Skeleton>
        </Text> */}
      </Flex>

      <Grid templateColumns='repeat(3, 1fr)'>
        <GridItem>
          <Heading as='h6' size='sm'>
            Sites ({sites.length || 0})
          </Heading>

          <Flex marginTop='5' flexDirection='column'>
            {sitesQueryLoading ? (
              <Grid gap='4' templateColumns='436px 1fr'>
                {range(10).map((_, index) => (
                  <>
                    <Skeleton
                      h='90px'
                      w='100%'
                      isLoaded={false}
                      key={index}
                      data-testid='app-skeleton-placeholder'
                    />
                    <div></div>
                  </>
                ))}
              </Grid>
            ) : (
              <>
                {sites.length < 1 ? (
                  <VStack spacing='4' my='5'>
                    <Text variant='subtle' data-testid='no-tenants-message'>
                      No sites available.
                    </Text>
                  </VStack>
                ) : (
                  <>
                    {sites?.map((site, idx) => {
                      const subTitleNoSpaces =
                        site.id?.replaceAll(' ', '-') ?? '';

                      return (
                        <Grid
                          templateColumns='auto 1fr auto'
                          alignItems='center'
                          justifyContent='space-between'
                          top='0px'
                        >
                          <GridItem paddingTop={idx === 0 ? '0' : '4'}>
                            <SiteCard
                              onMouseEnter={() => siteOnHover(site, idx)}
                              onMouseLeave={() => siteOnHover(null)}
                              appName={site.id}
                              destinations={selectedSite?.destinations?.length}
                              selected={
                                selectedDestination?.sites?.some(
                                  (el: any) => el?.id === site.id,
                                ) || selectedSite?.site?.id === site.id
                              }
                              onClick={() => siteOnClick(site)}
                              data-testid={`site-card-${idx}`}
                              data-behavior-analytics-id={`site-card_ga_${site.id}_${subTitleNoSpaces}`}
                              customerEnvironmentType=''
                              active={site.enabled}
                              events={1000}
                            ></SiteCard>
                          </GridItem>

                          <GridItem
                            paddingTop={idx === 0 ? '0' : '4'}
                            borderTopRightRadius={idx === 0 ? '8' : 0}
                            borderBottomRightRadius={
                              idx > 0 && idx === sites.length - 1 ? '8' : 0
                            }
                            borderTopWidth={idx === 0 ? '1px' : '0'}
                            borderBottomWidth={idx === 0 ? '0' : '1px'}
                            borderRightWidth={
                              idx === 0 || idx === sites.length - 1
                                ? '1px'
                                : '0'
                            }
                            borderStyle='solid'
                            borderColor={
                              selectedDestination?.sites?.some(
                                (el: any) => el?.id === site.id,
                              ) || selectedSite?.site?.id === site.id
                                ? 'purple.500'
                                : 'neutral.100'
                            }
                            w='100%'
                            h='50%'
                            alignSelf={idx !== 0 ? 'start' : 'end'}
                          ></GridItem>

                          <Flex direction='column' h='100%'>
                            <GridItem
                              h='50%'
                              borderRightWidth={
                                idx === 0 || idx === sites.length - 1
                                  ? '0'
                                  : '1px'
                              }
                              borderStyle='solid'
                              borderColor={
                                selectedDestination?.lastSiteIndex >= idx ||
                                (selectedSite?.index >= idx && idx !== 0) ||
                                selectedSite?.site?.id === site.id
                                  ? 'purple.500'
                                  : 'neutral.100'
                              }
                            ></GridItem>
                            <GridItem
                              h='50%'
                              borderRightWidth={
                                idx === 0 || idx === sites.length - 1
                                  ? '0'
                                  : '1px'
                              }
                              borderStyle='solid'
                              borderColor={
                                (selectedDestination?.lastSiteIndex &&
                                  selectedDestination?.lastSiteIndex > idx) ||
                                (selectedSite?.index > idx && idx !== 0)
                                  ? 'purple.500'
                                  : 'neutral.100'
                              }
                            >
                              {' '}
                            </GridItem>
                          </Flex>
                        </Grid>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Flex>
        </GridItem>

        <GridItem marginTop='5'>
          <Flex
            marginTop='7'
            justifyContent='space-between'
            paddingTop='6'
            h='fit-content'
            alignItems='center'
          >
            <GridItem
              borderTopWidth={sites.length < 1 ? '0px' : '1px'}
              borderStyle='solid'
              borderColor={
                (selectedDestination?.destination?.id &&
                  selectedDestination.lastSiteIndex >= 0) ||
                selectedSite?.site?.id
                  ? 'purple.500'
                  : 'neutral.100'
              }
              w='100%'
            ></GridItem>

            <Flex
              background='white'
              borderWidth='1px'
              borderStyle='solid'
              boxShadow='base'
              alignItems='center'
              justifyContent='center'
              p='6'
              height='fit-content'
              cursor='pointer'
              borderColor={
                selectedDestination?.destination?.id || selectedSite?.site?.id
                  ? 'purple.500'
                  : 'transparent'
              }
              _hover={{ backgroundColor: 'neutral.100' }}
              borderRadius='md'
              transitionProperty='common'
              transitionDuration='normal'
              minW='192px'
            >
              <Image w='125px' src={IconLinks.sitecoreLogo} />
            </Flex>

            <GridItem
              borderTopWidth={filteredDest.length < 1 ? '0px' : '1px'}
              borderStyle='solid'
              borderColor={
                selectedDestination?.destination?.id ||
                (selectedSite?.site?.id && selectedSite?.lastDestIndex >= 0)
                  ? 'purple.500'
                  : 'neutral.100'
              }
              w='100%'
            ></GridItem>
          </Flex>
        </GridItem>

        <GridItem>
          <Heading as='h6' size='sm' width='436px' marginLeft='auto'>
            Destinations ({filteredDest.length || 0})
          </Heading>

          <Flex marginTop='4' flexDirection='column'>
            {tenantsQueryLoading || destinationsQueryLoading ? (
              <Grid gap='4' templateColumns='1fr 436px'>
                {range(10).map((_, index) => (
                  <>
                    <div></div>
                    <Skeleton
                      h='90px'
                      w='100%'
                      isLoaded={false}
                      key={index}
                      data-testid='app-skeleton-placeholder'
                    />
                  </>
                ))}
              </Grid>
            ) : (
              <>
                {filteredDest.length < 1 ? (
                  <NoTenantsPanelMessage />
                ) : (
                  <>
                    {filteredDest?.map((dest, idx, arr) => {
                      const subTitleNoSpaces = dest.displayName
                        ? dest.displayName.replaceAll(' ', '-')
                        : '';

                      return (
                        <Grid
                          templateColumns='auto 1fr auto'
                          alignItems='center'
                          justifyContent='space-between'
                        >
                          <Flex direction='column' h='100%'>
                            <GridItem
                              h='50%'
                              borderLeftWidth={
                                idx === 0 || idx === arr.length - 1
                                  ? '0'
                                  : '1px'
                              }
                              borderStyle='solid'
                              borderLeftColor={
                                selectedDestination?.index >= idx ||
                                selectedSite?.lastDestIndex >= idx
                                  ? 'purple.500'
                                  : 'neutral.100'
                              }
                            ></GridItem>
                            <GridItem
                              h='50%'
                              borderLeftWidth={
                                idx === 0 || idx === arr.length - 1
                                  ? '0'
                                  : '1px'
                              }
                              borderStyle='solid'
                              borderLeftColor={
                                selectedDestination?.index > idx ||
                                selectedSite?.lastDestIndex > idx
                                  ? 'purple.500'
                                  : 'neutral.100'
                              }
                            >
                              {' '}
                            </GridItem>
                          </Flex>

                          <GridItem
                            paddingTop={idx === 0 ? '0' : '4'}
                            borderTopLeftRadius={idx === 0 ? '8' : 0}
                            borderBottomLeftRadius={
                              idx > 0 && idx === arr.length - 1 ? '8' : 0
                            }
                            borderTopWidth={idx === 0 ? '1px' : '0'}
                            borderBottomWidth={idx === 0 ? '0' : '1px'}
                            borderLeftWidth={
                              idx === 0 || idx === arr.length - 1 ? '1px' : '0'
                            }
                            borderStyle='solid'
                            borderColor={
                              selectedDestination?.destination?.id ===
                                dest.id ||
                              selectedSite?.destinations?.some(
                                (el: any) => el?.id === dest.id,
                              )
                                ? 'purple.500'
                                : 'neutral.100'
                            }
                            w='100%'
                            h='50%'
                            alignSelf={idx !== 0 ? 'start' : 'end'}
                          ></GridItem>

                          <GridItem paddingTop={idx === 0 ? '0' : '4'}>
                            <DestinationCard
                              data-testid={`destination-card-${idx}`}
                              data-behavior-analytics-id={`destination-card_ga_${dest.name}_${subTitleNoSpaces}`}
                              appName={dest.name}
                              selected={
                                selectedDestination?.destination?.id ===
                                  dest.id ||
                                selectedSite?.destinations?.some(
                                  (el: any) => el?.id === dest.id,
                                )
                              }
                              sites={selectedDestination?.sites?.length}
                              tenantName={dest.displayName || ''}
                              key={`${dest.name}_${dest.displayName || ''}`}
                              onClick={() => destinationOnClick(dest.id)}
                              onMouseEnter={() => destinationOnHover(dest, idx)}
                              onMouseLeave={() => destinationOnHover(null)}
                              {...(dest.icon?.src && {
                                image: dest.icon.src,
                              })}
                              customerEnvironmentType={
                                dest.customerEnvironmentType
                              }
                              active={true}
                            />
                          </GridItem>
                        </Grid>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default UnifiedTracking;
